/**
 * Tasks → Today landing.
 *
 * Default sub-route under `/tasks`. PR #41 promoted Today to the index
 * landing (Board moved to `/tasks/board`) and rebuilt it as stacked
 * tables instead of the M5 list-style sections. The Round-5 rewrite
 * (UAT-44) extended that pattern to four sections so closed work had a
 * visible home on the same view that surfaces today's frogs and due
 * items. UAT-47 stripped that back to three: the Completed-this-week
 * section moved to the Table tab (PR #48 owns that surface) so Today
 * reads as a focused daily summary rather than a four-section ledger.
 *
 * Sections (rendered top-to-bottom):
 *   - FROGS: open tasks where `frogDates` includes today's calendar date
 *   - DUE TODAY: open tasks where `dueDate` equals today's calendar date
 *   - COMPLETED TODAY: tasks closed at any time today
 *
 * Frog and due-today filters tolerate both the wire-format
 * `YYYY-MM-DD` and the full ISO datetime forms that the backend may
 * emit for `frogDates` — the comparator slices to the first ten
 * characters and matches on the date prefix. The UAT-44 bug came from
 * relying on `Array.includes(YYYY-MM-DD)` which never matched a stored
 * `2026-05-29T00:00:00.000Z` and silently dropped frogged rows.
 *
 * Reference: spec section 15.5 (Tasks views), Claude Design Files
 * README section 5.3 (Today view).
 */

import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';

import { useCompletedTasksSince, useTasks } from '@/lib/api/tasks';
import { todayIso } from '@/lib/utils';
import { TaskTable } from '@/components/tasks/TaskTable';
import type { Task } from '@workspace/shared';

export const Route = createFileRoute('/tasks/')({
  component: TasksTodayView,
});

/**
 * Tolerant date-prefix comparator.
 *
 * `frogDates` is stored as a Postgres timestamp column and the wire
 * shape can come back either as the bare `YYYY-MM-DD` calendar date or
 * as the full ISO datetime `2026-05-29T00:00:00.000Z`. Comparing the
 * first ten characters covers both forms so the Today filter survives
 * either backend serialisation.
 */
function isSameDay(candidate: string, dayIso: string): boolean {
  return candidate.slice(0, 10) === dayIso;
}

/**
 * Today's date in UTC, formatted as `YYYY-MM-DD`.
 *
 * The Today landing's frog filter has to tolerate the gap between
 * Brisbane local time (UTC+10) and the backend's UTC-anchored
 * `startOfTodayUtc()` write path. When Peter frogs a task at 9am
 * Brisbane on 2026-05-29 the backend stores the frog with the UTC
 * calendar date 2026-05-28, but the shared `todayIso()` helper
 * returns the local date 2026-05-29 — the equality check then drops
 * the row. Accepting either anchor lets the filter render the
 * morning's frogs without waiting on the broader timezone-handling
 * cleanup the backend still needs.
 */
function todayIsoUtc(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(
    d.getUTCDate(),
  ).padStart(2, '0')}`;
}

/**
 * Today landing view. Exported so the component test can render the
 * view without spinning up a TanStack Router context. The Route
 * definition above is the only production consumer.
 */
export function TasksTodayView() {
  const { data, isLoading, isError } = useTasks();

  // Cache today's wire-format date so the comparator helpers run once
  // per render rather than reparsing inside every filter callback.
  // `today` is the Brisbane-local date; `todayUtc` is the same wall
  // clock expressed in UTC so the frog filter can match either anchor
  // (see the timezone note on `todayIsoUtc` above).
  const today = todayIso();
  const todayUtc = todayIsoUtc();

  // Pull today's closed-task bucket independently of the open list so
  // the section is paged with its own ceiling and the open kanban /
  // table never has to drag completed rows across the wire.
  const completedToday = useCompletedTasksSince(today);

  // Partition the open task list into the two scheduled buckets. We
  // skip completed and archived rows because Today is forward-looking:
  // a task closed earlier today belongs in the Completed today section
  // below, not the open Frogs / Due-today sections.
  const { frogs, dueToday } = useMemo(() => {
    const tasks = data?.tasks ?? [];
    const open = tasks.filter((t) => t.completedAt === null && t.archivedAt === null);
    return {
      // Frog filter accepts the local-date and UTC-date anchors so a
      // morning Brisbane frog (stored UTC the prior day) still renders.
      // The full ISO datetime form is also covered by the slice in
      // `isSameDay`.
      frogs: open.filter((t) =>
        t.frogDates.some((d) => isSameDay(d, today) || isSameDay(d, todayUtc)),
      ),
      dueToday: open.filter((t) => t.dueDate !== null && isSameDay(t.dueDate, today)),
    } satisfies { frogs: Task[]; dueToday: Task[] };
  }, [data, today, todayUtc]);

  const completedTodayRows = completedToday.data?.tasks ?? [];

  if (isLoading) return <p className="mono-label text-mx-grey-mid">LOADING…</p>;
  if (isError) return <p className="text-mx-body-sm text-mx-neg">Could not load tasks.</p>;

  return (
    <div className="flex flex-col gap-mx-6">
      <Section eyebrowNum="A" label="Frogs" emptyCopy="Nothing frogged for today." rows={frogs} />
      <Section eyebrowNum="B" label="Due today" emptyCopy="Nothing due today." rows={dueToday} />
      <Section
        eyebrowNum="C"
        label="Completed today"
        emptyCopy="Nothing completed today yet."
        rows={completedTodayRows}
      />
    </div>
  );
}

/**
 * Named section wrapper used by the Today landing. Renders a numbered
 * mono-label eyebrow above the table so each section reads like a
 * MAXART numbered eyebrow on the rest of the shell. The empty state
 * keeps the copy inline so the section still has presence when nothing
 * matches.
 */
function Section({
  eyebrowNum,
  label,
  rows,
  emptyCopy,
}: {
  eyebrowNum: string;
  label: string;
  rows: Task[];
  emptyCopy: string;
}) {
  return (
    <section data-testid={`today-section-${eyebrowNum.toLowerCase()}`}>
      <p className="eyebrow mono-label mb-mx-2 text-mx-grey-mid">
        <span className="num">{eyebrowNum}</span>
        {label}
      </p>
      {rows.length === 0 ? (
        <p className="text-mx-body-sm text-mx-grey-mid">{emptyCopy}</p>
      ) : (
        <TaskTable rows={rows} showStatusColumn={false} emptyCopy={emptyCopy} />
      )}
    </section>
  );
}
